import { useEffect, useState } from "react";
import {
    adminDayZeroResetPreview,
    adminDayZeroResetExecute,
    adminOpsListBackups,
    adminOpsBackupRunNow,
    adminOpsBackupDrDrill,
    adminOpsBackupDrDrillLatest,
    adminInactiveWipePreview,
    adminInactiveWipeExecute,
} from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle, HardDrive, ShieldCheck, UserX } from "lucide-react";

function formatBytes(n) {
    if (!n) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
    return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatRelative(iso) {
    if (!iso) return "never";
    try {
        const secs = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
        if (secs < 60) return `${secs}s ago`;
        if (secs < 3600) return `${Math.floor(secs / 60)} min ago`;
        if (secs < 86400) return `${Math.floor(secs / 3600)} h ago`;
        return `${Math.floor(secs / 86400)} d ago`;
    } catch {
        return iso;
    }
}

// Day-Zero Reset card — lives inside an Admin "Danger zone" section.
// Two-step UX:
//   1. Click "Show what will be wiped" → calls preview, shows counts.
//   2. Click "Reset to Day 1" → opens modal demanding the literal "RESET"
//      string + the admin password. Both are re-validated server-side.
//
// Bonus: also surfaces the weekly auto-backup cron status + a "Run backup
// now" button so admins can verify backups are actually being captured.
export default function DayZeroReset() {
    const [preview, setPreview] = useState(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [confirmText, setConfirmText] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [backups, setBackups] = useState(null);
    const [backupBusy, setBackupBusy] = useState(false);
    const [drillBusy, setDrillBusy] = useState(false);
    const [drillReport, setDrillReport] = useState(null);
    const [drillExpanded, setDrillExpanded] = useState(false);
    const [lastDrill, setLastDrill] = useState(null);

    const loadBackups = async () => {
        try {
            const r = await adminOpsListBackups();
            setBackups(r);
        } catch {
            /* non-blocking — endpoint may not exist on older deploys */
        }
    };

    const loadLastDrill = async () => {
        try {
            const r = await adminOpsBackupDrDrillLatest();
            setLastDrill(r?.last || null);
        } catch {
            /* non-blocking */
        }
    };

    useEffect(() => {
        loadBackups();
        loadLastDrill();
    }, []);

    const runBackupNow = async () => {
        setBackupBusy(true);
        try {
            const r = await adminOpsBackupRunNow();
            if (r.ok) {
                toast.success(`Backup saved · ${formatBytes(r.size_bytes)}`);
            } else {
                toast.warning(`Backup failed: ${r.error || "unknown"}`);
            }
            loadBackups();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Backup failed");
        } finally {
            setBackupBusy(false);
        }
    };

    const runDrDrill = async () => {
        setDrillBusy(true);
        setDrillReport(null);
        try {
            const r = await adminOpsBackupDrDrill();
            setDrillReport(r);
            setDrillExpanded(true);
            if (r.ok) {
                const s = r.summary || {};
                toast.success(
                    `DR drill ✓ · ${s.collections_in_backup}/${s.collections_in_live} collections · ${s.backup_total_docs?.toLocaleString()} docs`
                );
            } else {
                toast.warning(
                    `DR drill found ${r.summary?.critical_gaps ?? "?"} gap(s)${r.error ? ` · ${r.error.slice(0, 80)}` : ""}`
                );
            }
            loadLastDrill();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "DR drill failed");
        } finally {
            setDrillBusy(false);
        }
    };

    const loadPreview = async () => {
        setLoadingPreview(true);
        try {
            const r = await adminDayZeroResetPreview();
            setPreview(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load preview");
        } finally {
            setLoadingPreview(false);
        }
    };

    const reset = async () => {
        setBusy(true);
        setResult(null);
        try {
            const r = await adminDayZeroResetExecute({
                confirm_text: confirmText,
                password,
            });
            setResult(r);
            if (r.ok) {
                toast.success(
                    `Day-Zero Reset complete — ${r.total_deleted.toLocaleString()} rows deleted`,
                );
            } else {
                toast.warning("Reset finished with errors — see results below.");
            }
            setModalOpen(false);
            setConfirmText("");
            setPassword("");
            // refresh preview to show zeros
            loadPreview();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset failed");
        } finally {
            setBusy(false);
        }
    };

    // ---- Inactive-user wipe state + handlers --------------------------------
    const [inactivePreview, setInactivePreview] = useState(null);
    const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
    const [inactiveConfirmText, setInactiveConfirmText] = useState("");
    const [inactivePassword, setInactivePassword] = useState("");
    const [inactiveBusy, setInactiveBusy] = useState(false);
    const [inactiveResult, setInactiveResult] = useState(null);
    const [inactiveEmailsExpanded, setInactiveEmailsExpanded] = useState(false);

    const loadInactivePreview = async () => {
        try {
            const r = await adminInactiveWipePreview();
            setInactivePreview(r);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to load preview");
        }
    };

    const executeInactiveWipe = async () => {
        setInactiveBusy(true);
        setInactiveResult(null);
        try {
            const r = await adminInactiveWipeExecute({
                confirm_text: inactiveConfirmText,
                password: inactivePassword,
            });
            setInactiveResult(r);
            if (r.ok) {
                toast.success(
                    `Wiped ${r.users_deleted} inactive user${r.users_deleted === 1 ? "" : "s"} · ${r.total_deleted.toLocaleString()} total rows`,
                );
            } else {
                toast.warning("Wipe finished with errors — see results below.");
            }
            setInactiveModalOpen(false);
            setInactiveConfirmText("");
            setInactivePassword("");
            loadInactivePreview();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Wipe failed");
        } finally {
            setInactiveBusy(false);
        }
    };

    const topWiped = preview
        ? [...(preview.wiped || [])]
            .sort((a, b) => b.count - a.count)
            .slice(0, 10)
        : [];

    return (
        <div
            className="bl-card p-5 mt-6 border-2 border-[var(--bl-rust)]"
            data-testid="admin-dayzero-card"
        >
            <div className="flex items-start gap-3">
                <AlertTriangle
                    className="w-5 h-5 text-[var(--bl-rust)] mt-0.5 shrink-0"
                    aria-hidden
                />
                <div className="flex-1">
                    <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--bl-rust)] font-semibold">
                        Danger zone · Test data reset
                    </div>
                    <div className="mt-1 text-sm text-bl-muted">
                        Wipes <b className="text-bl">only test accounts</b> (emails
                        matching <code className="text-[11px]">@example.com</code>,{" "}
                        <code className="text-[11px]">tester@</code>,{" "}
                        <code className="text-[11px]">+test@</code>, etc.) and
                        every row they own across uploads, reports, transactions,
                        sessions, audit logs &amp; support tickets.{" "}
                        <b className="text-bl">Real user accounts stay untouched.</b>{" "}
                        Also clears ephemeral telemetry (preflight snapshots,
                        IP cache, daily canary alerts) and contact messages from
                        test addresses. A{" "}
                        <code className="text-[11px]">mongodump</code> tarball is
                        written to <code className="text-[11px]">/app/backups/</code>{" "}
                        before the wipe so the operation is reversible.
                    </div>
                </div>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    onClick={loadPreview}
                    disabled={loadingPreview}
                    className="bl-btn-ghost text-xs"
                    data-testid="admin-dayzero-preview-btn"
                >
                    {loadingPreview
                        ? "Loading…"
                        : preview
                            ? "Refresh preview"
                            : "Show what will be wiped"}
                </button>
                {preview && (
                    <button
                        type="button"
                        onClick={() => setModalOpen(true)}
                        className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[var(--bl-rust)] text-white hover:opacity-90 transition-opacity"
                        data-testid="admin-dayzero-open-modal-btn"
                    >
                        Wipe test data →
                    </button>
                )}
            </div>

            {preview && (
                <div
                    className="mt-4 grid md:grid-cols-2 gap-4"
                    data-testid="admin-dayzero-preview"
                >
                    <div className="border border-bl rounded-md p-3">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            Will be wiped
                        </div>
                        <div className="mt-1 font-display font-semibold text-2xl text-[var(--bl-rust)] tnum">
                            {preview.total_wipe_rows.toLocaleString()}
                        </div>
                        <div className="text-[11px] text-bl-muted">
                            rows across {preview.total_wipe_collections} collections{" "}
                            + {preview.users_to_wipe.toLocaleString()} test users
                        </div>
                        <ul className="mt-3 text-[11px] space-y-1">
                            {topWiped.map((c) => (
                                <li
                                    key={c.name}
                                    className="flex justify-between gap-2"
                                    data-testid={`admin-dayzero-wipe-${c.name}`}
                                >
                                    <span className="font-mono-bl text-bl truncate">
                                        {c.name}
                                    </span>
                                    <span className="text-bl-muted tnum">
                                        {c.count.toLocaleString()}
                                    </span>
                                </li>
                            ))}
                            {(preview.wiped || []).length > 10 && (
                                <li className="text-bl-muted italic">
                                    …and {preview.wiped.length - 10} more
                                </li>
                            )}
                        </ul>
                    </div>
                    <div className="border border-bl rounded-md p-3 bg-[var(--bl-moss-tint)]">
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            Will be preserved
                        </div>
                        <div className="mt-1 font-display font-semibold text-2xl text-[var(--bl-moss)] tnum">
                            {(preview.preserved || []).length}
                        </div>
                        <div className="text-[11px] text-bl-muted">
                            collections — admin + real user accounts stay
                        </div>
                        <ul className="mt-3 text-[11px] space-y-1 max-h-44 overflow-auto">
                            {(preview.preserved || []).map((c) => (
                                <li
                                    key={c.name}
                                    className="flex justify-between gap-2"
                                    data-testid={`admin-dayzero-preserve-${c.name}`}
                                >
                                    <span className="font-mono-bl text-bl truncate">
                                        {c.name}
                                        {c.note && (
                                            <span className="ml-1 text-bl-muted">
                                                ({c.note})
                                            </span>
                                        )}
                                    </span>
                                    <span className="text-bl-muted tnum">
                                        {c.count.toLocaleString()}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>
                    {/* Test accounts list — full-width below the two columns */}
                    <div
                        className="md:col-span-2 border border-bl rounded-md p-3 bg-bl-bg"
                        data-testid="admin-dayzero-test-users"
                    >
                        <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted">
                            Test accounts matched ({preview.test_user_count || 0})
                        </div>
                        {(preview.test_users || []).length === 0 ? (
                            <div className="mt-2 text-[11px] text-bl-muted italic">
                                No test accounts found. Nothing user-scoped will be deleted —
                                only ephemeral telemetry will be cleared.
                            </div>
                        ) : (
                            <ul className="mt-2 text-[11px] space-y-0.5 max-h-44 overflow-auto">
                                {preview.test_users.map((u) => (
                                    <li
                                        key={u.user_id}
                                        className="flex justify-between gap-2"
                                        data-testid={`admin-dayzero-test-user-${u.user_id}`}
                                    >
                                        <span className="font-mono-bl text-bl truncate">
                                            {u.email}
                                        </span>
                                        <span className="text-bl-muted tnum shrink-0">
                                            {formatRelative(u.created_at)}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>
            )}

            {result && (
                <div
                    className={
                        "mt-4 border rounded-md p-3 "
                        + (result.ok
                            ? "bg-[var(--bl-moss-tint)] border-[var(--bl-moss)]"
                            : "bg-[var(--bl-rust-tint)] border-[var(--bl-rust)]")
                    }
                    data-testid="admin-dayzero-result"
                >
                    <div className="font-display font-semibold text-bl">
                        {result.ok
                            ? `Reset complete — ${result.total_deleted.toLocaleString()} rows deleted`
                            : "Reset finished with errors"}
                    </div>
                    <div className="mt-1 text-xs text-bl-muted">
                        Backup:{" "}
                        {result.backup?.ok
                            ? `✓ ${result.backup.path}`
                            : `⚠ ${result.backup?.error || "skipped"}`}{" "}
                        · users wiped: {result.users_deleted}
                    </div>
                </div>
            )}

            {/* Inactive-user wipe panel — separate from the test-pattern
                Day-Zero scrub. Deletes every non-admin user who hasn't
                been active today and has never paid. Catches QA/scraper
                signups whose emails look real but never engaged. */}
            <div
                className="mt-4 border border-[var(--bl-rust)]/30 rounded-md p-3 bg-[var(--bl-rust-tint)]"
                data-testid="admin-inactive-wipe"
            >
                <div className="flex items-start gap-2">
                    <UserX
                        className="w-4 h-4 text-[var(--bl-rust)] mt-0.5 shrink-0"
                        aria-hidden
                    />
                    <div className="min-w-0 flex-1">
                        <div className="font-display font-semibold text-bl">
                            Wipe inactive users
                        </div>
                        <div className="text-xs text-bl-muted mt-0.5">
                            Deletes every non-admin account that has{" "}
                            <b>no activity today (UTC)</b> AND <b>has never paid</b>.
                            Catches the random sign-ups that don&apos;t match the
                            test-pattern regex. Mongodump backup is written first.
                        </div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                        <button
                            type="button"
                            onClick={loadInactivePreview}
                            className="bl-btn-ghost text-xs"
                            data-testid="admin-inactive-wipe-preview-btn"
                        >
                            {inactivePreview ? "Refresh" : "Show preview"}
                        </button>
                        {inactivePreview?.users_to_wipe > 0 && (
                            <button
                                type="button"
                                onClick={() => setInactiveModalOpen(true)}
                                className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[var(--bl-rust)] text-white hover:opacity-90 transition-opacity"
                                data-testid="admin-inactive-wipe-open-modal-btn"
                            >
                                Wipe {inactivePreview.users_to_wipe} →
                            </button>
                        )}
                    </div>
                </div>

                {inactivePreview && (
                    <div
                        className="mt-3 text-[11px] bg-bl-surface border border-bl rounded p-2"
                        data-testid="admin-inactive-wipe-preview"
                    >
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-bl">
                            <span>
                                <b className="text-[var(--bl-rust)] text-xs">
                                    {inactivePreview.users_to_wipe}
                                </b>{" "}
                                inactive user{inactivePreview.users_to_wipe === 1 ? "" : "s"}
                            </span>
                            <span className="text-bl-muted">
                                ({inactivePreview.total_rows_to_wipe.toLocaleString()} total rows
                                across {inactivePreview.cascade_collection_counts?.length || 0} collections)
                            </span>
                            <span className="text-[var(--bl-moss)]">
                                · preserving {inactivePreview.preserve_counts?.total_preserved_users || 0} (
                                {inactivePreview.preserve_counts?.admin || 0} admin
                                {(inactivePreview.preserve_counts?.paid || 0) > 0
                                    ? ` + ${inactivePreview.preserve_counts.paid} paid`
                                    : ""}
                                {(inactivePreview.preserve_counts?.active_session_today || 0) > 0
                                    ? ` + ${inactivePreview.preserve_counts.active_session_today} active`
                                    : ""}
                                )
                            </span>
                        </div>
                        {inactivePreview.wipe_user_emails?.length > 0 && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setInactiveEmailsExpanded((v) => !v)}
                                    className="mt-1.5 text-[var(--bl-teal)] underline"
                                    data-testid="admin-inactive-wipe-emails-toggle"
                                >
                                    {inactiveEmailsExpanded ? "Hide" : "Show"} email list (
                                    {inactivePreview.wipe_user_emails.length})
                                </button>
                                {inactiveEmailsExpanded && (
                                    <ul className="mt-1.5 max-h-40 overflow-auto font-mono-bl">
                                        {inactivePreview.wipe_user_emails.map((em) => (
                                            <li key={em} className="text-bl truncate">
                                                {em}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </>
                        )}
                    </div>
                )}

                {inactiveResult && (
                    <div
                        className={
                            "mt-3 text-[11px] border rounded p-2 "
                            + (inactiveResult.ok
                                ? "bg-[var(--bl-moss-tint)] border-[var(--bl-moss)]/30"
                                : "bg-[var(--bl-rust-tint)] border-[var(--bl-rust)]/30")
                        }
                        data-testid="admin-inactive-wipe-result"
                    >
                        <div className="font-display font-semibold text-bl">
                            {inactiveResult.ok
                                ? `✓ Wiped ${inactiveResult.users_deleted} user(s) · ${inactiveResult.total_deleted.toLocaleString()} total rows`
                                : "Wipe finished with errors"}
                        </div>
                        <div className="mt-0.5 text-bl-muted">
                            Backup:{" "}
                            {inactiveResult.backup?.ok
                                ? `✓ ${inactiveResult.backup.path}`
                                : `⚠ ${inactiveResult.backup?.error || "skipped"}`}
                        </div>
                    </div>
                )}
            </div>

            {/* Backups panel (always visible — surfaces weekly cron status) */}
            <div
                className="mt-4 border border-bl rounded-md p-3 bg-bl-bg"
                data-testid="admin-dayzero-backups"
            >
                {/* Top-line health pill — green / amber / red based on the
                    age of the latest object in R2. Catches a silently broken
                    cron before you ever need to restore from one. */}
                {backups?.health && (
                    <div
                        className="mb-3 flex items-center gap-2"
                        data-testid="admin-backup-health"
                    >
                        <span
                            className={
                                "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.16em] uppercase font-medium border " + (
                                    {
                                        healthy: "bg-[var(--bl-moss-tint)] text-[var(--bl-moss)] border-[var(--bl-moss)]/30",
                                        stale: "bg-[var(--bl-amber-tint)] text-[var(--bl-amber)] border-[var(--bl-amber)]/40",
                                        red: "bg-[var(--bl-rust-tint)] text-[var(--bl-rust)] border-[var(--bl-rust)]/40",
                                        unknown: "bg-bl-bg text-bl-muted border-bl",
                                    }[backups.health.status] || "bg-bl-bg text-bl-muted border-bl"
                                )
                            }
                            data-testid={`admin-backup-health-${backups.health.status}`}
                        >
                            <span
                                className={
                                    "w-1.5 h-1.5 rounded-full " + (
                                        {
                                            healthy: "bg-[var(--bl-moss)]",
                                            stale: "bg-[var(--bl-amber)]",
                                            red: "bg-[var(--bl-rust)]",
                                            unknown: "bg-bl-muted",
                                        }[backups.health.status] || "bg-bl-muted"
                                    )
                                }
                            />
                            Backup health · {backups.health.status}
                        </span>
                        <span className="text-[11px] text-bl-muted">
                            {backups.health.message}
                        </span>
                    </div>
                )}
                <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex items-start gap-2">
                        <HardDrive
                            className="w-4 h-4 text-bl-muted mt-0.5 shrink-0"
                            aria-hidden
                        />
                        <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-bl-muted font-semibold">
                                Automated backups
                            </div>
                            <div className="text-[11px] text-bl-muted mt-0.5">
                                {backups?.enabled ? (
                                    <>
                                        <span className="text-[var(--bl-moss)] font-semibold">
                                            ON
                                        </span>{" "}
                                        · Sunday 07:00 UTC · keeps last 4
                                    </>
                                ) : (
                                    <>
                                        <span className="text-[var(--bl-rust)] font-semibold">
                                            OFF
                                        </span>{" "}
                                        · set <code>WEEKLY_BACKUP_ENABLED=true</code> in{" "}
                                        <code>backend/.env</code> to enable
                                    </>
                                )}
                                {backups?.last_weekly_run && (
                                    <>
                                        {" · last run: "}
                                        <span
                                            className={
                                                backups.last_weekly_run.ok
                                                    ? "text-[var(--bl-moss)]"
                                                    : "text-[var(--bl-rust)]"
                                            }
                                        >
                                            {backups.last_weekly_run.ok ? "✓" : "!"}{" "}
                                            {formatRelative(backups.last_weekly_run.started_at)}
                                        </span>
                                    </>
                                )}
                            </div>
                            <div
                                className="text-[10px] text-bl-muted mt-1"
                                data-testid="admin-dayzero-backup-s3-status"
                            >
                                Off-pod replication:{" "}
                                {backups?.s3_offsite_enabled ? (
                                    <span className="text-[var(--bl-moss)] font-semibold">
                                        ON
                                    </span>
                                ) : (
                                    <span className="text-[var(--bl-amber)] font-semibold">
                                        OFF
                                    </span>
                                )}
                                {backups?.s3_offsite_enabled ? (
                                    <>
                                        {" · "}
                                        <code>
                                            {backups.s3_endpoint === "aws-s3"
                                                ? "AWS S3"
                                                : backups.s3_endpoint?.replace(
                                                    "https://",
                                                    "",
                                                )}
                                        </code>{" "}
                                        · bucket{" "}
                                        <code>{backups.s3_bucket}</code>
                                        {backups?.last_weekly_run?.s3_upload && (
                                            <>
                                                {" · last upload: "}
                                                <span
                                                    className={
                                                        backups.last_weekly_run.s3_upload.ok
                                                            ? "text-[var(--bl-moss)]"
                                                            : "text-[var(--bl-rust)]"
                                                    }
                                                >
                                                    {backups.last_weekly_run.s3_upload.ok ? "✓" : "!"}
                                                    {!backups.last_weekly_run.s3_upload.ok && (
                                                        <>
                                                            {" "}
                                                            {backups.last_weekly_run.s3_upload.error?.slice(
                                                                0, 60,
                                                            )}
                                                        </>
                                                    )}
                                                </span>
                                            </>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        {" · set "}
                                        <code>BACKUP_S3_BUCKET</code> +{" "}
                                        <code>BACKUP_S3_ACCESS_KEY_ID</code> +{" "}
                                        <code>BACKUP_S3_SECRET_ACCESS_KEY</code>{" "}
                                        to enable
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={runBackupNow}
                        disabled={backupBusy}
                        className="bl-btn-ghost text-xs"
                        data-testid="admin-dayzero-backup-run-btn"
                    >
                        {backupBusy ? "Backing up…" : "Run backup now"}
                    </button>
                </div>
                {backups?.backups?.length > 0 && (
                    <ul
                        className="mt-2 text-[11px] space-y-0.5 max-h-32 overflow-auto"
                        data-testid="admin-dayzero-backups-list"
                    >
                        {backups.backups.slice(0, 6).map((b) => (
                            <li
                                key={b.name}
                                className="flex justify-between gap-2"
                                data-testid={`admin-dayzero-backup-${b.kind}`}
                            >
                                <span className="font-mono-bl text-bl truncate">
                                    <span
                                        className={
                                            "inline-block px-1 mr-1.5 rounded text-[9px] uppercase "
                                            + (b.kind === "weekly"
                                                ? "bg-[var(--bl-moss-tint)] text-[var(--bl-moss)]"
                                                : b.kind === "predayzero"
                                                    ? "bg-[var(--bl-rust-tint)] text-[var(--bl-rust)]"
                                                    : "bg-bl-bg text-bl-muted")
                                        }
                                    >
                                        {b.kind}
                                    </span>
                                    {b.name}
                                </span>
                                <span className="text-bl-muted tnum shrink-0">
                                    {formatBytes(b.size_bytes)} · {formatRelative(b.mtime)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}

                {/* Disaster-recovery drill — pulls latest backup from R2 +
                    counts docs vs live DB so the admin can verify the
                    rollback path actually works before they need it. */}
                <div
                    className="mt-3 pt-3 border-t border-bl-line"
                    data-testid="admin-dayzero-dr-drill"
                >
                    <div className="flex items-start justify-between gap-3">
                        <div className="flex items-start gap-2 min-w-0">
                            <ShieldCheck
                                className="w-4 h-4 text-[var(--bl-teal)] mt-0.5 shrink-0"
                                aria-hidden
                            />
                            <div className="text-xs text-bl-muted min-w-0">
                                <div className="font-display font-semibold text-bl">
                                    Disaster-recovery drill
                                </div>
                                <div className="mt-0.5">
                                    Pulls the latest backup from{" "}
                                    {backups?.s3_offsite_enabled ? "R2" : "local disk"},
                                    counts docs per collection, and diffs vs the live DB.
                                    Read-only — confirms your rollback path works.
                                    {lastDrill && (
                                        <>
                                            {" · Last: "}
                                            <span
                                                className={
                                                    lastDrill.ok
                                                        ? "text-[var(--bl-moss)] font-semibold"
                                                        : "text-[var(--bl-rust)] font-semibold"
                                                }
                                            >
                                                {lastDrill.ok ? "✓" : "✗"}{" "}
                                                {formatRelative(lastDrill.finished_at || lastDrill.started_at)}
                                            </span>
                                            {lastDrill.summary?.critical_gaps > 0 && (
                                                <> · {lastDrill.summary.critical_gaps} gap(s)</>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={runDrDrill}
                            disabled={drillBusy}
                            className="bl-btn-ghost text-xs shrink-0"
                            data-testid="admin-dayzero-dr-drill-run-btn"
                        >
                            {drillBusy ? "Running…" : "Run DR drill"}
                        </button>
                    </div>

                    {drillReport && (
                        <div
                            className="mt-2 text-[11px] bg-bl-bg rounded border border-bl-line p-2"
                            data-testid="admin-dayzero-dr-drill-report"
                        >
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                                <span
                                    className={
                                        drillReport.ok
                                            ? "px-1.5 py-0.5 rounded font-display font-semibold uppercase text-[9px] bg-[var(--bl-moss-tint)] text-[var(--bl-moss)]"
                                            : "px-1.5 py-0.5 rounded font-display font-semibold uppercase text-[9px] bg-[var(--bl-rust-tint)] text-[var(--bl-rust)]"
                                    }
                                >
                                    {drillReport.ok ? "✓ RESTORABLE" : "✗ GAPS FOUND"}
                                </span>
                                {drillReport.tarball && (
                                    <span className="font-mono-bl text-bl-muted truncate">
                                        {drillReport.source === "r2" ? "R2: " : "local: "}
                                        {drillReport.tarball}
                                    </span>
                                )}
                                {drillReport.dump_format && (
                                    <span className="text-bl-muted">
                                        fmt: {drillReport.dump_format}
                                    </span>
                                )}
                                {drillReport.duration_seconds != null && (
                                    <span className="text-bl-muted">
                                        {drillReport.duration_seconds}s
                                    </span>
                                )}
                            </div>
                            {drillReport.summary && (
                                <div className="mt-1 text-bl-muted">
                                    {drillReport.summary.collections_in_backup} collections in
                                    backup · {drillReport.summary.collections_in_live} live ·{" "}
                                    {drillReport.summary.backup_total_docs?.toLocaleString()}{" "}
                                    backup docs · {drillReport.summary.live_total_docs?.toLocaleString()}{" "}
                                    live docs
                                    {drillReport.summary.critical_gaps > 0 && (
                                        <span className="text-[var(--bl-rust)] font-semibold">
                                            {" "}· {drillReport.summary.critical_gaps} critical gap(s)
                                        </span>
                                    )}
                                </div>
                            )}
                            {drillReport.error && (
                                <div className="mt-1 text-[var(--bl-rust)]">
                                    {drillReport.error}
                                </div>
                            )}
                            {drillReport.collections?.length > 0 && (
                                <>
                                    <button
                                        type="button"
                                        className="mt-1 text-[var(--bl-teal)] underline text-[11px]"
                                        onClick={() => setDrillExpanded((v) => !v)}
                                        data-testid="admin-dayzero-dr-drill-toggle"
                                    >
                                        {drillExpanded ? "Hide" : "Show"} per-collection diff
                                        ({drillReport.collections.length})
                                    </button>
                                    {drillExpanded && (
                                        <ul
                                            className="mt-1 max-h-48 overflow-auto space-y-0.5 font-mono-bl"
                                            data-testid="admin-dayzero-dr-drill-list"
                                        >
                                            {drillReport.collections.map((c) => (
                                                <li
                                                    key={c.collection}
                                                    className="flex justify-between gap-2"
                                                >
                                                    <span className="truncate">
                                                        <span
                                                            className={
                                                                "inline-block px-1 mr-1 rounded text-[9px] uppercase "
                                                                + (c.status === "match"
                                                                    ? "bg-[var(--bl-moss-tint)] text-[var(--bl-moss)]"
                                                                    : c.status === "drift_normal" || c.status === "empty_both" || c.status === "missing_from_live"
                                                                        ? "bg-bl-bg text-bl-muted"
                                                                        : "bg-[var(--bl-rust-tint)] text-[var(--bl-rust)]")
                                                            }
                                                        >
                                                            {c.status.replace(/_/g, " ")}
                                                        </span>
                                                        {c.collection}
                                                    </span>
                                                    <span className="text-bl-muted tnum shrink-0">
                                                        bk {c.backup_count} / live {c.live_count}
                                                        {c.delta !== 0 && (
                                                            <span className={c.delta > 0 ? "text-bl-muted" : "text-[var(--bl-rust)]"}>
                                                                {" "}({c.delta > 0 ? "+" : ""}{c.delta})
                                                            </span>
                                                        )}
                                                    </span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Confirmation modal */}
            {modalOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                    data-testid="admin-dayzero-modal"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setModalOpen(false);
                    }}
                >
                    <div className="bl-card p-6 max-w-md w-full border-2 border-[var(--bl-rust)]">
                        <div className="flex items-center gap-2 text-[var(--bl-rust)] font-display font-semibold text-lg">
                            <AlertTriangle className="w-5 h-5" aria-hidden />
                            Confirm test-data reset
                        </div>
                        <p className="mt-3 text-sm text-bl">
                            This will delete{" "}
                            <b className="text-[var(--bl-rust)]">
                                {preview?.total_wipe_rows.toLocaleString()} rows
                            </b>
                            {preview?.test_user_count ? (
                                <>
                                    {" "}belonging to{" "}
                                    <b className="text-[var(--bl-rust)]">
                                        {preview.test_user_count} test account
                                        {preview.test_user_count === 1 ? "" : "s"}
                                    </b>
                                </>
                            ) : (
                                <></>
                            )}
                            . Real user accounts will not be touched. A backup
                            tarball will be written to{" "}
                            <code className="text-[11px]">/app/backups/</code> first.
                        </p>
                        <label className="block mt-4 text-xs font-semibold text-bl">
                            Type{" "}
                            <code className="px-1.5 py-0.5 bg-bl-bg border border-bl rounded text-[var(--bl-rust)]">
                                RESET
                            </code>{" "}
                            to confirm
                        </label>
                        <input
                            type="text"
                            value={confirmText}
                            onChange={(e) => setConfirmText(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-bl rounded-md bg-bl-surface text-bl font-mono-bl"
                            placeholder="RESET"
                            data-testid="admin-dayzero-confirm-input"
                            autoComplete="off"
                        />
                        <label className="block mt-3 text-xs font-semibold text-bl">
                            Admin password
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-bl rounded-md bg-bl-surface text-bl"
                            placeholder="••••••••"
                            data-testid="admin-dayzero-password-input"
                            autoComplete="current-password"
                        />
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setModalOpen(false);
                                    setConfirmText("");
                                    setPassword("");
                                }}
                                className="bl-btn-ghost text-sm"
                                disabled={busy}
                                data-testid="admin-dayzero-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={reset}
                                disabled={
                                    busy || confirmText !== "RESET" || !password
                                }
                                className="px-4 py-2 rounded-md text-sm font-semibold bg-[var(--bl-rust)] text-white disabled:opacity-40 hover:opacity-90 transition-opacity"
                                data-testid="admin-dayzero-execute-btn"
                            >
                                {busy ? "Wiping…" : "Execute reset"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Inactive-user wipe confirmation modal */}
            {inactiveModalOpen && (
                <div
                    className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
                    data-testid="admin-inactive-wipe-modal"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setInactiveModalOpen(false);
                    }}
                >
                    <div className="bl-card p-6 max-w-md w-full border-2 border-[var(--bl-rust)]">
                        <div className="flex items-center gap-2 text-[var(--bl-rust)] font-display font-semibold text-lg">
                            <AlertTriangle className="w-5 h-5" aria-hidden />
                            Confirm inactive-user wipe
                        </div>
                        <p className="mt-3 text-sm text-bl">
                            This will delete{" "}
                            <b className="text-[var(--bl-rust)]">
                                {(inactivePreview?.users_to_wipe || 0).toLocaleString()} user
                                {inactivePreview?.users_to_wipe === 1 ? "" : "s"}
                            </b>{" "}
                            ({(inactivePreview?.total_rows_to_wipe || 0).toLocaleString()} total
                            rows including cascaded data). Admins, paying customers, and
                            anyone active today are preserved. A backup tarball is written
                            to <code className="text-[11px]">/app/backups/preinactivewipe_*</code> first.
                        </p>
                        <label className="block mt-4 text-xs font-semibold text-bl">
                            Type{" "}
                            <code className="px-1.5 py-0.5 bg-bl-bg border border-bl rounded text-[var(--bl-rust)]">
                                WIPE
                            </code>{" "}
                            to confirm
                        </label>
                        <input
                            type="text"
                            value={inactiveConfirmText}
                            onChange={(e) => setInactiveConfirmText(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-bl rounded-md bg-bl-surface text-bl font-mono-bl"
                            placeholder="WIPE"
                            data-testid="admin-inactive-wipe-confirm-input"
                            autoComplete="off"
                        />
                        <label className="block mt-3 text-xs font-semibold text-bl">
                            Admin password
                        </label>
                        <input
                            type="password"
                            value={inactivePassword}
                            onChange={(e) => setInactivePassword(e.target.value)}
                            className="mt-1 w-full px-3 py-2 border border-bl rounded-md bg-bl-surface text-bl"
                            placeholder="••••••••"
                            data-testid="admin-inactive-wipe-password-input"
                            autoComplete="current-password"
                        />
                        <div className="mt-5 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setInactiveModalOpen(false);
                                    setInactiveConfirmText("");
                                    setInactivePassword("");
                                }}
                                className="bl-btn-ghost text-sm"
                                disabled={inactiveBusy}
                                data-testid="admin-inactive-wipe-cancel-btn"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={executeInactiveWipe}
                                disabled={
                                    inactiveBusy
                                    || inactiveConfirmText !== "WIPE"
                                    || !inactivePassword
                                }
                                className="px-4 py-2 rounded-md text-sm font-semibold bg-[var(--bl-rust)] text-white disabled:opacity-40 hover:opacity-90 transition-opacity"
                                data-testid="admin-inactive-wipe-execute-btn"
                            >
                                {inactiveBusy ? "Wiping…" : "Execute wipe"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
